import { locationsApi } from 'api/locations'
import Billboard from 'components/Billboard'
import FeedbackTable from 'components/FeedbackTable'
import TablePaginationFooter from 'components/TablePaginationFooter'
import TableToolbar, { ToolbarLocationOption } from 'components/TableToolbar'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useFlag from 'hooks/useFlag'
import { ROIStats, useROICalculator } from 'hooks/useROICalculator'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { currencyFormatter } from 'utilities/formatters'
import { getViewPath, useOrderRatings } from './useOrderRatings'

const t = buildTranslate('order_ratings')

// Kill-switch: the ROI formula is wired end-to-end to merchant.purchase_volume,
// but we're not confident in the numbers yet — it's self-reported volume plus
// a placeholder $24.57 AOV (the mock's example implies ~$45). Flip to true to
// bring the stats back once the inputs are trusted; the planned upgrade is
// measured order volume/avg ticket via the recovery roi_projection endpoint.
const SHOW_ROI_STATS = false

// Billboard already bakes in mb-l (32px) for Overview's shadow-clearance; this
// tab needs 48px total down to the toolbar, so the remaining 16px is added
// here rather than changing Billboard's margin (which Overview also relies on).
const BILLBOARD_TOOLBAR_GAP_PX = 16

// Maps the calculator's raw numbers onto Billboard's pre-formatted stat contract
// (the component formats nothing itself). Extracted and exported so the dollar
// formatting, the shared "/month" suffix and the two labels stay under test while
// SHOW_ROI_STATS keeps them out of the DOM — a rendered assertion can't reach them.
export const buildRoiStats = (
  roi: ROIStats | null
): React.ComponentProps<typeof Billboard>['stats'] =>
  roi
    ? [
        {
          value: currencyFormatter(roi.laborSavings, false),
          suffix: t('billboard.stat_suffix'),
          label: t('billboard.stat_labor_savings_label'),
        },
        {
          value: currencyFormatter(roi.recoveredRevenue, false),
          suffix: t('billboard.stat_suffix'),
          label: t('billboard.stat_revenue_recovered_label'),
        },
      ]
    : undefined

// The Details tab: the shared toolbar → FeedbackTable → pagination footer. All
// three table components are W0's; this pane supplies only plain data + copy.
//
// No white card behind them (confirmed against Figma 2026-08-16): they sit
// directly on the tab's grey background so the table's left edge lines up with
// the page title and the banner above it, rather than being inset by card padding.
//
// The "Explore RecoveryAI" upsell Billboard is shown only when the recovery-ai
// flag is on, so the CTA has a destination — every /recovery_ai route redirects
// a flag-off merchant home, the dead-end that got the billboard pulled in #3835.
// "Explorable by all users" remains AI-733, at which point this gate loosens to
// all users.
const DetailsPane = () => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const recoveryAiEnabled = useFlag('recovery-ai', false)

  const merchant = useCurrentMerchant()
  // The Fields type declares purchase_volume: number (non-nullable), but the
  // underlying DB column is nullable — factories (createMerchant) default it
  // to null, and merchants who haven't filled in onboarding volume have no
  // value at runtime. Don't trust the type; guard explicitly.
  const monthlyOrders = merchant?.purchase_volume ?? null
  const roi = useROICalculator(monthlyOrders)

  const {
    searchValue,
    onSearchChange,
    hideResolved,
    onHideResolvedChange,
    locationId,
    onLocationChange,
    rows,
    isLoading,
    isError,
    onRetry,
    sortDirection,
    onSortChange,
    hasActiveFilters,
    page,
    pageSize,
    totalCount,
    totalPages,
    onPageChange,
    onPageSizeChange,
  } = useOrderRatings()

  const { data: locationsData } = locationsApi.useGetLocationsQuery({
    coming_soon: false,
  })

  // Options come from api/locations, not the rows — a location with no
  // conversation-backed ratings must still be selectable. Label = name, falling
  // back to street when name is blank (the RAI table uses name, and the mock
  // reads like a name).
  const locationOptions = useMemo<ToolbarLocationOption[]>(
    () =>
      (locationsData?.locations ?? []).map(location => ({
        id: location.id,
        label: location.name || location.street,
      })),
    [locationsData]
  )

  // Kept alive alongside useROICalculator so the wiring (merchant ->
  // roi -> formatted stats) stays exercised by tc/lint even while
  // SHOW_ROI_STATS keeps it out of the rendered Billboard; the mapping itself is
  // covered directly by buildRoiStats' unit test.
  const roiStats = useMemo(() => buildRoiStats(roi), [roi])

  return (
    <>
      {recoveryAiEnabled && (
        <div className={css({ marginBottom: `${BILLBOARD_TOOLBAR_GAP_PX}px` })}>
          <Billboard
            eyebrow={t('billboard.eyebrow')}
            ctaLabel={t('billboard.cta')}
            onCtaClick={() => dispatch(push('/recovery_ai'))}
            headlineEmphasis
            // Stats render only when the merchant's self-reported
            // purchase_volume exists (an onboarding estimate, maintained via
            // ActiveAdmin — not measured). The headline + CTA always render
            // regardless. A real measured average ticket (and eventually a
            // measured order volume) is expected from the roi_projection
            // endpoint as a follow-up. Gated behind SHOW_ROI_STATS above
            // until those inputs are trusted.
            stats={SHOW_ROI_STATS ? roiStats : undefined}
          />
        </div>
      )}
      {/* The card's padding used to supply this gap; the toolbar owns it now. */}
      <div className="mb-m">
        <TableToolbar
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchAriaLabel={t('toolbar.search_aria')}
          hideResolved={hideResolved}
          onHideResolvedChange={onHideResolvedChange}
          hideResolvedLabel={t('toolbar.hide_resolved')}
          locations={locationOptions}
          selectedLocationId={locationId}
          onLocationChange={onLocationChange}
        />
      </div>
      <FeedbackTable
        rows={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        sortDirection={sortDirection}
        onSortChange={onSortChange}
        getViewPath={getViewPath}
        // This tab's RatingDetail reads location.state.row to paint instantly
        // instead of blocking on its own fetch — the only Feedback surface that
        // does, so it's the only one that opts into carrying the row. Rows WITH a
        // conversation link to RAI CaseDetail instead, which never reads the
        // state, so they carry none: no customer name / free-text comment goes
        // into window.history.state for a destination that ignores it.
        getLinkState={row => (row.conversationId ? undefined : { row })}
        // Figma TUTD spec for this tab's Resolved-by column; RAI Escalations
        // now opts in too (Figma MX-RecoveryAI node 2318:64225) — only
        // CaseDetail's RecentActivities keeps the legacy solid pill.
        subtleResolvedBadge
        // Only this tab's wire (order ratings) carries the customer's free-text
        // comment; the RAI Escalations wire has no such field, so it leaves the
        // column off rather than rendering a column of em-dashes.
        showCommentColumn
        // This tab's order-feedback ratings have no location dimension worth
        // showing, unlike RAI Escalations (which keeps the default). Comment
        // becomes this surface's sole unpinned flex column once Location is
        // hidden (see the INVARIANT comment on FeedbackTable's COLUMN_WIDTH_PX).
        showLocationColumn={false}
        emptyMessage={t('table.empty')}
        filteredEmptyMessage={t('table.filtered_empty')}
        errorMessage={t('table.error')}
        hasActiveFilters={hasActiveFilters}
      />
      <TablePaginationFooter
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  )
}

export default DetailsPane
